<script>
    /*
    	AuthServiceListItem.svelte

        9/7 - no more base class - just include all fields.
        9/8 - we may need to make individual stores for each expanded item!
        9/13 - must set CSS scroll-margin-top: 55px to scroll below header

        When expanded these will fetch service data and set item.serviceData.
        They do not refetch after the first fetch is successful.

        This is the JSON for each (lightweight) ServiceIndex item:

        {
            distance: 0.0000025453394187761702
            distanceUnit: "mi"
            exclusive-requirements: []
            postal-code: "63103-1522"
            score: 0
            service-description: "NA - Migration"
            service-id: "4974"
            service-location: {lat: 38.6332, lon: -90.2164}
            service-name: "Heart of St. Louis Adult Day Health Center"
            taxonomies: {
                tcare-taxonomy-1: ['aging services', 'adult day care']
            }
        }

        Note that this IS NOT the full service data that is fetched when expanded.

    */

    import { onMount } from 'svelte';
    import { writable, get } from 'svelte/store';
    import { push } from 'svelte-spa-router';

    import { masterTaxonomies, masterService } from '../../common/master-objects.js';         // new 9/8    
    import { fetchServiceById2 } from '../../api/services.js';
	import { setErrorAlert } from '../../stores/AlertsStore';
	import { currentListItemId } from '../../stores/ScreenSettingsStore';
    import { ServiceObject } from '../../common/connect-classes.js';        // each item makes its own

    import { AccountStore } from "../../common/account.ts";
    import { getIsPreferredPartner } from "../../common/utils.js";

    // TCARE components
    import TaxonomyFieldsGroup from '../field-groups/TaxonomyFieldsGroup.svelte';
    import ServiceFieldsGroup1 from '../field-groups/ServiceFieldsGroup1.svelte';
    import ServiceFieldsGroup2 from '../field-groups/ServiceFieldsGroup2.svelte';
    import ToggleStateButton from '../buttons/ToggleStateButton.svelte';
    import LearnMore from '../common/LearnMore.svelte';
	import TCareRatingStars from "../tcare-ui/TCareRatingStars.svelte";

    // SMUI components
    import Checkbox from '@smui/checkbox';
	import { Icon } from '@smui/icon-button';
	import { Svg } from '@smui/common/elements';
	import { mdiMapMarkerOutline, mdiBookmarkOutline } from '@mdi/js';
    import Button, { Label } from '@smui/button';
    // import StarRating from 'svelte-star-rating';

    const EVEN_ROW_STYLE = 'background-color: #F1EFF1';
    const ODD_ROW_STYLE  = 'background-color: #F9F7FA';     // or #F6F4F6?
    const SEL_ROW_STYLE  = 'background-color: #FFF3F5';

    const SEARCH_ERROR_MSG = 'We\'re sorry - we could not fetch those details.';

    const SVG_EXPANDED_PATH  = '/svg-icons/nav-icons/list-item-open.svg';
    const SVG_COLLAPSED_PATH = '/svg-icons/nav-icons/list-item-closed.svg';
    const SVG_CONNECT_PATH   = '/svg-icons/button-icons/molecular-1.svg';
    
	// --tcui-primary: #611C73;
	// --tcui-secondary: #FE4672;
    const STAR_COLOR = '#611C73';           // TCareRatingStars need a starColor prop

    // const starRatingConfig = {
    //     fullColor: 'var(--tcare-secondary)',
    //     size: 16
    // };    

    export let item;                        // a Service index in this case
    export let index;                       // for odd-even row colors
    export let onCheckboxClick;             // handled in the parent List
    export let isCheckbox;                  // show or hide the item's Checkbox

    $: formattedDistance = getFormattedDistance(item);
    $: listItemStyle = getListItemStyle(item);
    $: isShowingLearnMore = false;
    $: isPreferredPartner = getIsPreferredPartner(item);

    onMount(() => {
        // need to range check the RatingStars component (?)
        if (item['average-rating'] < 0) item['average-rating'] = 0;
        if (item['average-rating'] > 5) item['average-rating'] = 5;
        if (item['service-description'].length < 3) item['service-description'] = '';
    })

    const getFormattedDistance = (item) => {
        let dist = item['distance'];
        let unit = item['distanceUnit'];
        if (dist === null) return 'online';
        if (dist < 1.0) return 'less than 1' + ' ' + unit;
        return dist.toFixed(1) + ' ' + unit;
    }

    // odd-even row colors
    const getListItemStyle = (item) => {
        let style = ODD_ROW_STYLE;
        if (index % 2 == 0) style = EVEN_ROW_STYLE;
        if (item.isSelected) style = SEL_ROW_STYLE;
        return style;
    }

    // THIS NEEDS TO BE ASYNC
    // currently we are setting masterService after the fetch - rethink this!
    // masterService should be reset after ANY CLICK on the ListItem
    const fetchService = async () => {
        // console.log('BaseServiceListItem fetching service: ' + item['service-id']);
        let json = null;

        if ($AccountStore?.isLoggedIn) {
            json = await fetchServiceById2(item['service-id']);
        } else {
            return;
        }

        if (json.errors) {
            setErrorAlert(SEARCH_ERROR_MSG, 3000);
            item.serviceData = null;                                // new 9/8
            item.isExpanded = false;                                // close it - no fetch
            item.isFetched = false;
        } else {
            item.serviceData = json;                                // new 9/8
            item.isExpanded = true;                                 // just to make sure
            item.isFetched = true;
            // each item has it's own copy of serviceData
            get(item.serviceStore).copyApiDataFrom(item.serviceData);    // new 9/8

            // fetching will always update the masterService store
            $masterService.clearApiData();
            $masterService.copyApiDataFrom(item.serviceData);
            $masterService = $masterService;                // refresh the store
        }
    }

    const _onCheckboxClick = (e) => {
        e.stopPropagation();
        onCheckboxClick(item);                                      // handler on List (onItemCheckboxClick)
    }

    const toggleIsExpanded = () => {
        item.isExpanded = !item.isExpanded;
        if (item.isExpanded && !item.isFetched) {                 // has never fetched
            // create a store just for this item
            item.serviceStore = writable(new ServiceObject('serviceStore-' + item['service-id']));
            get(item.serviceStore).copyTaxonomies($masterTaxonomies);
            fetchService();                                         // async call - will populate store
        }

        // update masterService store on any click
        if (item.serviceData) {
            $masterService.clearApiData();
            $masterService.copyApiDataFrom(item.serviceData);
            $masterService = $masterService;                // refresh the store
        }
    }

    // the ServiceReadPage will abort to LoginPage if needed
    const viewDetailsPage = () => {
        $currentListItemId = item['service-id'];
        push("/details/" + $currentListItemId);
    }

    // the ServiceUpdatePage will abort to LoginPage if needed
    const viewUpdatePage = () => {
        $currentListItemId = item['service-id'];
        push("/update/" + $currentListItemId);
    }

    // 10/17 - per Linsey request - clickable name
    const _onServiceNameClick = () => {
        viewDetailsPage();
    }

    // TODO: NEED TO SET THE $masterService - could be clicking another item's button!
    // TODO: change /referrals route to take a service id
    const _onConnectClick = () => {
        $currentListItemId = item['service-id'];
        // update masterService store on any click
        if (item.serviceData) {
            $masterService.clearApiData();
            $masterService.copyApiDataFrom(item.serviceData);
            $masterService = $masterService;                // refresh the store
        }
        push('/referrals');
    }
    
    // TODO: NEED TO SET THE $masterService - could be clicking another item's button!
    // TODO: change /referrals route to take a service id
    const _onStartReferralClick = () => {
        $currentListItemId = item['service-id'];
        // update masterService store on any click
        if (item.serviceData) {
            $masterService.clearApiData();
            $masterService.copyApiDataFrom(item.serviceData);
            $masterService = $masterService;                // refresh the store
        }
        push('/referrals');
    }

</script>

<style>
    .auth-list-item-wrapper {
        scroll-margin-top: 55px;            /* 55 header + 0 menu */
        width: 100%;
        margin-bottom: 5px;
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        border-radius: 5px;
    }

    .service-list-item {
        width: 95%;
        padding: 5px 0px;
        font-size: 1em;
        font-weight: normal;
        color: var(--tcare-primary);

        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-start;

        border-radius: 5px;
        border: 1px solid var(--tcare-white);
    }

    .service-list-item:hover {
        border: 1px solid var(--tcare-purple);
    }


    /* convert checkboxes to circles */
    :global(.check-box .mdc-checkbox__background) {
        border-radius: 50%;
    }

    :global(.service-list-item .mdc-button)  {
        min-width: 180px;
        margin: 10px;
        text-transform: none;
        background-color: var(--tcare-purple) !important;
        border-radius: 18px;
        overflow: hidden;
    }

    :global(.service-list-item svg)  {
        width: 24px;
        height: 24px;
        fill: var(--tcare-primary);
    }

    /* checkbox */
    .check-box {
        width:  5%; 
        margin-top: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
    }   

    :global(.check-box .mdc-checkbox) {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        /* border: 1px solid red; */
    }

    :global(.check-box .mdc-checkbox__background) {
        width: 20px;
        height: 20px;
    }

    

    /* expand/collapse */
    .cell-0 { 
        width:  5%; 
        text-align: center;
    }


    /* name and descr stacked */
    .cell-1 { 
        width: 65%; 
        text-align: left; 
        font-size: 1.2em; 
        font-weight: bold;
    }

    .cell-1 .name { 
        font-size: 1.0em; 
        font-weight: bold;
        text-decoration: underline;
        cursor: pointer;
    }

    .cell-1 .descr { 
        font-size: .8em; 
        font-weight: normal;
    }

    .cell-2 { 
        width: 10%; 
    }

    .cell-3 { 
        width: 10%; 
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: .8em; 
    }

    .cell-4 { 
        width: 10%; 
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        font-size: .8em; 
    }

    /* need this for the stars to center align vertically */
    :global(.cell-4.star-rating div) {
        margin-bottom: 0px;
    }

    .expanded-fields {
        width: 90%;
        margin-left: 5%;
        margin-top: 20px;
        margin-bottom: 10px;
        /* border: 2px solid gold; */
    }

    .button-row {
        margin: 20px 0px;
    }

    .svg-connect {
        margin-left: 5px;
        margin-top: 5px;
    }

    @media screen and (max-width: 600px) {
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>


{#if item}
<div class="auth-list-item-wrapper" id={item['service-id']}>

    <div class="check-box">
        {#if isCheckbox}
            <Checkbox on:click={_onCheckboxClick} checked={item.isSelected}/>
        {/if}
    </div>
    
    <div class="service-list-item" style={listItemStyle}>

        <!-- collapse/expand 5% -->
        <div class="cell-0 flex-center expand-collapse" >
            <ToggleStateButton 
                state={item.isExpanded} 
                toggleFunction={toggleIsExpanded}
                svgTruePath={SVG_EXPANDED_PATH}
                svgFalsePath={SVG_COLLAPSED_PATH}
            />
            
        </div>

        <!-- name 65% (stacked with description) -->
        <div class="cell-1 ellipsis">
            <div class="name ellipsis" on:click={_onServiceNameClick}>
                {#if isPreferredPartner}
                    * {item['service-name']}
                {:else}
                    {item['service-name']}
                {/if}
            </div>
            <div class="descr ellipsis">
                {item['service-description']}
            </div>
        </div>


        <!-- postal 10% -->
        <div class="cell-2 flex-center">
            {item['postal-code']}
        </div> 

        <!-- distance 10% -->
        <div class="cell-3">
            <!-- <InlineSVG src={SVG_DISTANCE_PATH} /> -->
            <Icon component={Svg} viewBox="0 0 24 24" >
                <path d={mdiMapMarkerOutline} />
            </Icon>	    
            <div>{formattedDistance}</div>
        </div>

        <!-- average-rating 10% -->
        <div class="cell-4 star-rating">
            <!-- <StarRating rating={item['average-rating']} config={starRatingConfig} /> -->
            <TCareRatingStars 
                source={item} 
                prop={'average-rating'} 
                setValue={null}
                isReadOnly={true}
                starColor={STAR_COLOR}
            />

        </div>

        <!-- bookmark 5% -->
        <!--
        <div class="cell-4">
            <Icon component={Svg} viewBox="0 0 24 24" >
                <path d={mdiBookmarkOutline} />
            </Icon>	    
            <div>Bookmark</div>
        </div>
        -->

        {#if item.isExpanded && item.isFetched}
            <!-- in:fly="{{ y: -500, duration: 500 }}" out:fly="{{ y: -500, duration: 500 }}" -->
            <div class="expanded-fields">

                <div class="flex-row ai-start">
                    <TaxonomyFieldsGroup
                        store={item.serviceStore}
                        leftService={null}
                        rightService={null}
                        onCopyTaxonomy={null}
                        isEditable={false}
                        isOpen={true}
                        showTcareTax1={$AccountStore?.isLoggedIn}
                        showDiffs={false}
                    />
                </div>
            
                <div class="flex-row ai-start">
                    <ServiceFieldsGroup1
                        store={item.serviceStore}
                        leftService={null}
                        rightService={null}
                        isEditable={false}
                        onCopyValue={null}
                        isOpen={true}
                        showDiffs={false}
                    />
                </div>

                <div class="flex-row ai-start">
                    <ServiceFieldsGroup2
                        store={item.serviceStore}
                        leftService={null}
                        rightService={null}
                        isEditable={false}
                        onCopyValue={null}
                        isOpen={true}
                        showDiffs={false}
                    />
                </div>
            
                <div class="button-row flex-row jc-center">
                    <Button on:click={() => viewDetailsPage()} variant="raised" >
                        <Label>View Full Details</Label>
                    </Button>    
                    <Button on:click={() => viewUpdatePage()} variant="raised" >
                        <Label>Update Service</Label>
                    </Button>    

                    <!--
                    {#if isPreferredPartner}
                        <Button on:click={() => _onConnectClick()} 
                            variant="raised" 
                            style={'background-color: var(--tcui-aqua) !important; color: var(--tcui-primary) !important;'}
                        >
                            <Label>Connect</Label>
                            <img class="svg-connect" src={SVG_CONNECT_PATH} alt={'Connect'} draggable="false" >
                        </Button>
                    {:else}
                        <Button on:click={() => isShowingLearnMore = !isShowingLearnMore} 
                            variant="raised" 
                            style={'background-color: var(--tcui-aqua) !important; color: var(--tcui-primary) !important;'}
                        >
                            <Label>Learn More</Label>
                            <img class="svg-connect" src={SVG_CONNECT_PATH} alt={'Connect'} draggable="false" >
                        </Button>
                    {/if}
                    -->
               
                </div>

                {#if isShowingLearnMore}
                    <div class="spacer-h-20"></div>
                    <LearnMore serviceData={item.serviceData} onStartReferral={_onStartReferralClick}/>
                {/if}


            </div>      <!-- expanded-fields -->
        {/if}

    </div>
</div>

{/if}

